import React, { useState, useEffect } from 'react';
import { Hand, Palette, Key, User, Building2, CreditCard, PartyPopper, Leaf, Star, Zap, Lock, Upload, AlertTriangle, CheckCircle, Users, Bell, Loader } from 'lucide-react';

const API = 'https://qbdp-backend-production.up.railway.app/api';

const STEPS = [
  { id: 0, label: 'Bienvenue',     Icon: Hand         },
  { id: 1, label: 'Identité',      Icon: Palette      },
  { id: 2, label: 'Sécurité',      Icon: Key          },
  { id: 3, label: 'Employé',       Icon: User         },
  { id: 4, label: 'Zone',          Icon: Building2    },
  { id: 5, label: 'Abonnement',    Icon: CreditCard   },
  { id: 6, label: "C'est prêt !",  Icon: PartyPopper  },
];

const PLANS = [
  { id: 'starter',  label: 'Starter',  prix: 29,  employes: '0-25',    Icon: Leaf     },
  { id: 'pro',      label: 'Pro',      prix: 79,  employes: '0-100',   Icon: Star,    popular: true },
  { id: 'proplus',  label: 'Pro+',     prix: 149, employes: '0-200',   Icon: Zap      },
  { id: 'business', label: 'Business', prix: 249, employes: '200-500', Icon: Building2 },
];

function StepIndicator({ current, total }) {
  return (
    <div style={{ display: 'flex', gap: 6, justifyContent: 'center', marginBottom: 32 }}>
      {STEPS.slice(0, total).map((s, i) => (
        <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <div style={{
            width: 32, height: 32, borderRadius: '50%', display: 'flex', alignItems: 'center',
            justifyContent: 'center', transition: 'all .3s',
            background: i < current ? '#16a34a' : i === current ? '#1a56db' : '#f3f4f6',
            color: i <= current ? 'white' : '#9ca3af',
          }}>
            {i < current ? <CheckCircle size={16} /> : <s.Icon size={14} />}
          </div>
          {i < STEPS.length - 1 && (
            <div style={{ width: 24, height: 2, background: i < current ? '#16a34a' : '#e5e7eb', borderRadius: 2, transition: 'background .3s' }} />
          )}
        </div>
      ))}
    </div>
  );
}

export default function Onboarding({ user, onTerminer, onPasser }) {
  const [step, setStep]         = useState(0);
  const [loading, setLoading]   = useState(false);
  const [toast, setToast]       = useState(null);
  const token = sessionStorage.getItem('qbdp_token');

  // État étape 1 — Identité
  const [nom, setNom]           = useState('');
  const [message, setMessage]   = useState('');
  const [couleur, setCouleur]   = useState('#1a56db');
  const [logo, setLogo]         = useState(null);

  // État étape 2 — Code
  const [newCode, setNewCode]   = useState('');
  const [confirmCode, setConfirmCode] = useState('');
  const [showCode, setShowCode] = useState(false);

  // État étape 3 — Employé
  const [empNom, setEmpNom]     = useState('');
  const [empEmail, setEmpEmail] = useState('');
  const [empService, setEmpService] = useState('Direction');
  const [empSaved, setEmpSaved] = useState(false);

  // État étape 4 — Zone
  const [batNom, setBatNom]     = useState('');
  const [porteNom, setPorteNom] = useState('');
  const [zoneSaved, setZoneSaved] = useState(false);

  // État étape 5 — Abonnement
  const [planChoisi, setPlanChoisi] = useState('pro');
  const [facturation, setFacturation] = useState('mensuel');

  const showToast = (msg, ok = true) => {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 3000);
  };

  const saveStep = async (n) => {
    await fetch(`${API}/onboarding/step`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ step: n })
    });
  };

  const next = async () => {
    setLoading(true);
    try {
      // Sauvegarder selon l'étape
      if (step === 1) {
        await fetch(`${API}/personnalisation/parametres`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ nom_etablissement: nom, message_accueil: message, couleur_principale: couleur })
        });
        if (logo) {
          await fetch(`${API}/personnalisation/logo`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ logo_base64: logo })
          });
        }
      }
      if (step === 2 && newCode) {
        if (newCode !== confirmCode) { showToast('Les codes ne correspondent pas', false); setLoading(false); return; }
        if (newCode.length < 4) { showToast('Min. 4 caractères', false); setLoading(false); return; }
        await fetch(`${API}/personnalisation/reset-code`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ nouveau_code: newCode })
        });
      }
      if (step === 3 && empNom && empEmail && !empSaved) {
        const r = await fetch(`${API}/employes`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ nom: empNom, email: empEmail, service: empService, statut: 'actif' })
        });
        const d = await r.json();
        if (d.id) {
          setEmpSaved(true);
          // Envoyer invitation
          await fetch(`${API}/invitations/envoyer`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ employe_id: d.id })
          });
          showToast('✅ Employé ajouté et invitation envoyée !');
        }
      }
      if (step === 4 && batNom && !zoneSaved) {
        const r = await fetch(`${API}/batiments`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ nom: batNom })
        });
        const d = await r.json();
        if (d.id && porteNom) {
          await fetch(`${API}/batiments/${d.id}/portes`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ nom: porteNom, type: 'entree' })
          });
        }
        setZoneSaved(true);
        showToast('✅ Zone créée !');
      }

      await saveStep(step + 1);
      setStep(s => s + 1);
    } catch (err) { showToast('Erreur — ' + err.message, false); }
    setLoading(false);
  };

  const handleLogoFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 500000) { showToast('Max 500ko', false); return; }
    const reader = new FileReader();
    reader.onload = () => setLogo(reader.result);
    reader.readAsDataURL(file);
  };

  const handleCheckout = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/stripe-enterprise/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ plan: planChoisi, facturation })
      });
      const d = await r.json();
      if (d.url) window.location.href = d.url;
    } catch { showToast('Erreur Stripe', false); }
    setLoading(false);
  };

  const handleTerminer = async (path = null) => {
    await fetch(`${API}/onboarding/terminer`, {
      method: 'POST', headers: { Authorization: `Bearer ${token}` }
    });
    onTerminer(path);
  };

  const inp = { width: '100%', padding: '10px 14px', borderRadius: 10, border: '1px solid #e5e7eb', fontSize: 14, outline: 'none', boxSizing: 'border-box' };

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #f0f5ff 0%, #f9fafb 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>

      {/* Toast */}
      {toast && (
        <div style={{ position: 'fixed', top: 24, right: 24, zIndex: 2000, padding: '12px 20px', borderRadius: 12,
          background: toast.ok ? '#f0fdf4' : '#fef2f2', color: toast.ok ? '#16a34a' : '#dc2626',
          border: `1px solid ${toast.ok ? '#bbf7d0' : '#fecaca'}`, fontWeight: 600, fontSize: 13,
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>
          {toast.msg}
        </div>
      )}

      <div style={{ background: 'white', borderRadius: 24, padding: '40px 48px', width: '100%', maxWidth: step === 1 ? 960 : 640, boxShadow: '0 24px 64px rgba(0,0,0,0.1)', transition: 'max-width 0.3s' }}>

        {/* Logo QBDP */}
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <img src="/logo3.png" alt="qbdp" style={{ width: 64, height: 64, borderRadius: 16 }} />
        </div>

        <StepIndicator current={step} total={STEPS.length} />

        {/* ── ÉTAPE 0 : BIENVENUE ── */}
        {step === 0 && (
          <div style={{ textAlign: 'center' }}>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
              <div style={{ width: 72, height: 72, borderRadius: '50%', background: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Hand size={36} color="#1a56db" />
              </div>
            </div>
            <h1 style={{ fontSize: 26, fontWeight: 800, color: '#1a1a2e', marginBottom: 12 }}>
              Bienvenue sur QBDP !
            </h1>
            <p style={{ fontSize: 16, color: '#6b7280', lineHeight: 1.7, marginBottom: 32 }}>
              Bonjour <strong>{user?.nom}</strong> ! Nous allons configurer votre espace en <strong>5 minutes</strong>.
              Vous pourrez gérer vos accès, vos employés et personnaliser l'app à votre image.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 32, textAlign: 'left' }}>
              {[
                { Icon: Palette,   title: 'Personnalisez', desc: 'Logo, couleurs, identité de votre établissement' },
                { Icon: Key,       title: 'Sécurisez',     desc: 'Définissez votre code de connexion' },
                { Icon: User,      title: 'Invitez',       desc: 'Ajoutez votre premier employé' },
                { Icon: Lock,      title: 'Configurez',    desc: 'Créez vos zones et accès' },
              ].map(c => (
                <div key={c.title} style={{ background: '#f9fafb', borderRadius: 12, padding: '14px 16px', border: '1px solid #f3f4f6' }}>
                  <div style={{ marginBottom: 8 }}><c.Icon size={22} color="#1a56db" /></div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#1a1a2e' }}>{c.title}</div>
                  <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>{c.desc}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── ÉTAPE 1 : IDENTITÉ ── */}
        {step === 1 && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 280px', gap: 32, alignItems: 'start' }}>
            {/* Formulaire */}
            <div>
              <h2 style={{ fontSize: 22, fontWeight: 700, color: '#1a1a2e', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 10 }}><Palette size={22} color="#1a56db" /> Identité de votre établissement</h2>
              <p style={{ fontSize: 14, color: '#6b7280', marginBottom: 24 }}>Ces informations apparaîtront dans l'app mobile de vos employés et clients.</p>
              <div style={{ marginBottom: 16 }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>Nom de l'établissement *</label>
                <input style={inp} placeholder="Ex: Hôtel Bellevue, Clinique du Parc..." value={nom} onChange={e => setNom(e.target.value)} />
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>Message d'accueil</label>
                <input style={inp} placeholder="Bienvenue dans notre établissement !" value={message} onChange={e => setMessage(e.target.value)} />
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>Couleur principale</label>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  {['#1a56db','#7c3aed','#db2777','#dc2626','#d97706','#16a34a','#0891b2','#1a1a2e'].map(c => (
                    <div key={c} onClick={() => setCouleur(c)}
                      style={{ width: 32, height: 32, borderRadius: 8, background: c, cursor: 'pointer',
                        border: couleur === c ? '3px solid #1a1a2e' : '2px solid transparent' }} />
                  ))}
                  <input type="color" value={couleur} onChange={e => setCouleur(e.target.value)}
                    style={{ width: 40, height: 36, borderRadius: 8, border: '1px solid #e5e7eb', cursor: 'pointer', padding: 2 }} />
                </div>
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>Logo (optionnel)</label>
                <div onClick={() => document.getElementById('onb-logo').click()}
                  style={{ border: '2px dashed #e5e7eb', borderRadius: 10, padding: 16, textAlign: 'center', cursor: 'pointer' }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = '#1a56db'}
                  onMouseLeave={e => e.currentTarget.style.borderColor = '#e5e7eb'}>
                  {logo ? (
                    <img src={logo} alt="logo" style={{ height: 60, objectFit: 'contain', borderRadius: 8 }} />
                  ) : (
                    <>
                      <div style={{ display: 'flex', justifyContent: 'center' }}><Upload size={24} color="#9ca3af" /></div>
                      <div style={{ fontSize: 13, color: '#374151', marginTop: 4 }}>Cliquer pour uploader</div>
                      <div style={{ fontSize: 11, color: '#9ca3af' }}>PNG, JPG — max 500ko</div>
                    </>
                  )}
                </div>
                <input id="onb-logo" type="file" accept="image/*" style={{ display: 'none' }} onChange={handleLogoFile} />
              </div>
            </div>

            {/* Aperçu app mobile — identique à Personnalisation */}
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12 }}>Aperçu app mobile</div>
              <div style={{ border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden', background: '#f4f5f7', padding: '48px 12px 16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                {/* Carte header */}
                <div style={{ background: couleur, borderRadius: 16, padding: 16, position: 'relative', overflow: 'hidden' }}>
                  <div style={{ position: 'absolute', inset: 0, borderRadius: 16, background: '#1e40af', opacity: 0.35 }} />
                  <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 12 }}>
                    {logo
                      ? <img src={logo} alt="logo" style={{ width: 44, height: 44, borderRadius: 10, objectFit: 'contain' }} />
                      : <div style={{ width: 44, height: 44, borderRadius: 10, background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Building2 size={20} color="white" /></div>
                    }
                    <div style={{ flex: 1, color: 'white', fontWeight: 700, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      Bienvenue, Client · Ch. 101
                    </div>
                  </div>
                  <div style={{ position: 'relative', color: 'rgba(255,255,255,0.65)', fontSize: 11, marginTop: 8, paddingLeft: 56 }}>
                    Séjour jusqu'au 10 mai
                  </div>
                </div>
                {/* Carte NFC */}
                <div style={{ background: couleur, borderRadius: 16, padding: '28px 16px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 20, position: 'relative', overflow: 'hidden' }}>
                  <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.08)' }} />
                  <div style={{ position: 'relative', color: 'rgba(255,255,255,0.75)', fontSize: 11, fontWeight: 600 }}>Approchez du lecteur</div>
                  {logo
                    ? <img src={logo} alt="logo" style={{ width: 72, height: 72, objectFit: 'contain', position: 'relative' }} />
                    : <div style={{ width: 72, height: 72, borderRadius: 36, background: 'rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
                        <span style={{ color: 'white', fontWeight: 800, fontSize: 22 }}>{(nom || 'Q')[0]}</span>
                      </div>
                  }
                  <div style={{ position: 'relative', color: 'rgba(255,255,255,0.75)', fontSize: 10, fontWeight: 700, letterSpacing: 1.5 }}>OU CLIQUEZ</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── ÉTAPE 2 : SÉCURITÉ ── */}
        {step === 2 && (
          <div>
            <h2 style={{ fontSize: 22, fontWeight: 700, color: '#1a1a2e', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 10 }}><Key size={22} color="#1a56db" /> Code de connexion</h2>
            <p style={{ fontSize: 14, color: '#6b7280', marginBottom: 24 }}>
              Définissez votre code personnel de réinitialisation. Ce code vous permettra de réinitialiser votre personnalisation ou de récupérer l'accès en cas de problème.
            </p>
            <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10, padding: 12, marginBottom: 20, fontSize: 13, color: '#92400e', display: 'flex', alignItems: 'flex-start', gap: 8 }}>
              <AlertTriangle size={14} style={{ flexShrink: 0, marginTop: 1 }} /> Choisissez un code mémorable mais sécurisé. Notez-le quelque part de sûr.
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>Nouveau code</label>
              <div style={{ position: 'relative' }}>
                <input type={showCode ? 'text' : 'password'} style={{ ...inp, paddingRight: 44 }}
                  placeholder="Min. 4 caractères" value={newCode} onChange={e => setNewCode(e.target.value)} />
                <button type="button" onClick={() => setShowCode(v => !v)}
                  style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', lineHeight: 0, padding: 0 }}>
                  {showCode ? (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
                      <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
                      <line x1="1" y1="1" x2="23" y2="23"/>
                    </svg>
                  ) : (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                      <circle cx="12" cy="12" r="3"/>
                    </svg>
                  )}
                </button>
              </div>
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>Confirmer le code</label>
              <div style={{ position: 'relative' }}>
                <input type={showCode ? 'text' : 'password'} style={{ ...inp, paddingRight: 44 }}
                  placeholder="Répéter le code" value={confirmCode} onChange={e => setConfirmCode(e.target.value)} />
              </div>
            </div>
            <div style={{ marginTop: 12, fontSize: 12, color: '#6b7280' }}>
              Vous pouvez passer cette étape et le configurer plus tard dans Personnalisation.
            </div>
          </div>
        )}

        {/* ── ÉTAPE 3 : PREMIER EMPLOYÉ ── */}
        {step === 3 && (
          <div>
            <h2 style={{ fontSize: 22, fontWeight: 700, color: '#1a1a2e', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 10 }}><User size={22} color="#1a56db" /> Premier employé</h2>
            <p style={{ fontSize: 14, color: '#6b7280', marginBottom: 24 }}>
              Ajoutez votre premier employé. Il recevra une invitation par email pour créer son accès.
            </p>
            {empSaved ? (
              <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 12, padding: 20, textAlign: 'center' }}>
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 8 }}><CheckCircle size={36} color="#15803d" /></div>
                <div style={{ fontWeight: 600, color: '#15803d' }}>Invitation envoyée à {empEmail} !</div>
                <div style={{ fontSize: 13, color: '#6b7280', marginTop: 4 }}>Vous pourrez en ajouter d'autres depuis le dashboard.</div>
              </div>
            ) : (
              <>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>Nom complet *</label>
                  <input style={inp} placeholder="Nom Prénom" value={empNom} onChange={e => setEmpNom(e.target.value)} />
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>Email *</label>
                  <input style={inp} type="email" placeholder="employe@email.fr" value={empEmail} onChange={e => setEmpEmail(e.target.value)} />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>Service</label>
                  <select style={{ ...inp }} value={empService} onChange={e => setEmpService(e.target.value)}>
                    {['Direction','Resp. de secteur','Resp. d\'équipe','RH','Comptabilité','Commerce','Informatique','Maintenance','Accueil','Sécurité','Employé'].map(s => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
                <div style={{ marginTop: 12, fontSize: 12, color: '#6b7280' }}>Vous pouvez passer cette étape et ajouter des employés depuis le dashboard.</div>
              </>
            )}
          </div>
        )}

        {/* ── ÉTAPE 4 : ZONE ── */}
        {step === 4 && (
          <div>
            <h2 style={{ fontSize: 22, fontWeight: 700, color: '#1a1a2e', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 10 }}><Building2 size={22} color="#1a56db" /> Première zone d'accès</h2>
            <p style={{ fontSize: 14, color: '#6b7280', marginBottom: 24 }}>
              Créez votre premier bâtiment et sa première porte. Vous pourrez en ajouter autant que vous voulez depuis le dashboard.
            </p>
            {zoneSaved ? (
              <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 12, padding: 20, textAlign: 'center' }}>
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 8 }}><CheckCircle size={36} color="#15803d" /></div>
                <div style={{ fontWeight: 600, color: '#15803d' }}>Zone "{batNom}" créée avec la porte "{porteNom}" !</div>
                <div style={{ fontSize: 13, color: '#6b7280', marginTop: 4 }}>Assignez les accès depuis Accès & Zones.</div>
              </div>
            ) : (
              <>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>Nom du bâtiment *</label>
                  <input style={inp} placeholder="Ex: Bâtiment Principal, Siège, Hôtel..." value={batNom} onChange={e => setBatNom(e.target.value)} />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>Première porte</label>
                  <input style={inp} placeholder="Ex: Entrée principale, Parking, Ascenseur..." value={porteNom} onChange={e => setPorteNom(e.target.value)} />
                </div>
                <div style={{ marginTop: 12, fontSize: 12, color: '#6b7280' }}>Vous pouvez passer cette étape et configurer les zones depuis le dashboard.</div>
              </>
            )}
          </div>
        )}

        {/* ── ÉTAPE 5 : ABONNEMENT ── */}
        {step === 5 && (
          <div>
            <h2 style={{ fontSize: 22, fontWeight: 700, color: '#1a1a2e', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 10 }}><CreditCard size={22} color="#1a56db" /> Choisissez votre plan</h2>
            <p style={{ fontSize: 14, color: '#6b7280', marginBottom: 16 }}>
              15 jours d'essai gratuit inclus sur tous les plans. Aucune carte requise pour continuer l'essai.
            </p>
            <div style={{ display: 'flex', gap: 4, background: '#f3f4f6', borderRadius: 10, padding: 4, marginBottom: 20, width: 'fit-content' }}>
              {['mensuel','annuel'].map(f => (
                <button key={f} onClick={() => setFacturation(f)}
                  style={{ padding: '6px 16px', borderRadius: 8, border: 'none', fontSize: 12, fontWeight: 600, cursor: 'pointer',
                    background: facturation === f ? 'white' : 'transparent', color: facturation === f ? '#1a1a2e' : '#6b7280' }}>
                  {f === 'mensuel' ? 'Mensuel' : 'Annuel -17%'}
                </button>
              ))}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16 }}>
              {PLANS.map(p => {
                const prix = facturation === 'annuel' ? Math.round(p.prix * 0.83) : p.prix;
                return (
                  <div key={p.id} onClick={() => setPlanChoisi(p.id)}
                    style={{ border: `2px solid ${planChoisi === p.id ? '#1a56db' : '#e5e7eb'}`,
                      background: planChoisi === p.id ? '#eff6ff' : 'white',
                      borderRadius: 12, padding: '14px 16px', cursor: 'pointer', position: 'relative' }}>
                    {p.popular && <div style={{ position: 'absolute', top: -8, right: 12, background: '#1a56db', color: 'white', fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 8 }}>Populaire</div>}
                    <div style={{ marginBottom: 4 }}><p.Icon size={20} color={planChoisi === p.id ? '#1a56db' : '#6b7280'} /></div>
                    <div style={{ fontWeight: 700, fontSize: 14, marginTop: 4 }}>{p.label}</div>
                    <div style={{ fontSize: 20, fontWeight: 800, color: '#1a56db', margin: '4px 0 2px' }}>{prix}€<span style={{ fontSize: 12, fontWeight: 400, color: '#6b7280' }}>/mois</span></div>
                    <div style={{ fontSize: 11, color: '#6b7280', display: 'flex', alignItems: 'center', gap: 4 }}><User size={10} /> {p.employes} employés</div>
                  </div>
                );
              })}
            </div>
            <button onClick={handleCheckout} disabled={loading}
              style={{ width: '100%', padding: '12px', borderRadius: 10, border: 'none', background: '#1a56db', color: 'white', fontSize: 14, fontWeight: 600, cursor: 'pointer', marginBottom: 8 }}>
              {loading ? <Loader size={16} /> : `Commencer avec ${PLANS.find(p => p.id === planChoisi)?.label}`}
            </button>
            <button onClick={() => { next(); }} style={{ width: '100%', padding: '10px', borderRadius: 10, border: '1px solid #e5e7eb', background: 'white', fontSize: 13, color: '#6b7280', cursor: 'pointer' }}>
              Continuer l'essai gratuit →
            </button>
          </div>
        )}

        {/* ── ÉTAPE 6 : TERMINÉ ── */}
        {step === 6 && (
          <div style={{ textAlign: 'center' }}>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
              <div style={{ width: 80, height: 80, borderRadius: '50%', background: '#f0fdf4', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <PartyPopper size={40} color="#16a34a" />
              </div>
            </div>
            <h2 style={{ fontSize: 26, fontWeight: 800, color: '#1a1a2e', marginBottom: 12 }}>Votre espace est prêt !</h2>
            <p style={{ fontSize: 15, color: '#6b7280', lineHeight: 1.7, marginBottom: 32 }}>
              Félicitations ! Vous pouvez maintenant gérer vos accès, inviter vos employés et personnaliser votre expérience.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 32 }}>
              {[
                { Icon: Users,     label: 'Gérer les employés',     path: '/employes' },
                { Icon: Lock,      label: 'Configurer les accès',    path: '/acces' },
                { Icon: Bell,      label: 'Créer une campagne',      path: '/communications' },
                { Icon: Palette,   label: 'Personnalisation avancée',path: '/personnalisation' },
              ].map(l => (
                <div key={l.path} onClick={() => handleTerminer(l.path)}
                  style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', background: '#f9fafb', borderRadius: 10, border: '1px solid #e5e7eb', cursor: 'pointer', textAlign: 'left' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#eff6ff'}
                  onMouseLeave={e => e.currentTarget.style.background = '#f9fafb'}>
                  <l.Icon size={18} color="#1a56db" />
                  <span style={{ fontSize: 14, fontWeight: 500, color: '#1a1a2e' }}>{l.label}</span>
                  <span style={{ marginLeft: 'auto', color: '#1a56db' }}>→</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Navigation */}
        {step < 6 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 32, paddingTop: 20, borderTop: '1px solid #f3f4f6' }}>
            <div style={{ display: 'flex', gap: 10 }}>
              {step > 0 && (
                <button onClick={() => setStep(s => s - 1)}
                  style={{ padding: '10px 20px', borderRadius: 10, border: '1px solid #e5e7eb', background: 'white', fontSize: 13, cursor: 'pointer' }}>
                  ← Retour
                </button>
              )}
              {onPasser && (
                <button onClick={onPasser}
                  style={{ padding: '10px 16px', borderRadius: 10, border: 'none', background: 'none', fontSize: 12, color: '#9ca3af', cursor: 'pointer' }}>
                  Passer pour l'instant
                </button>
              )}
            </div>
            {step !== 5 && (
              <button onClick={next} disabled={loading}
                style={{ padding: '12px 28px', borderRadius: 10, border: 'none', background: '#1a56db', color: 'white', fontSize: 14, fontWeight: 600, cursor: 'pointer', opacity: loading ? 0.7 : 1 }}>
                {loading ? <Loader size={16} /> : step === 0 ? 'Commencer →' : 'Suivant →'}
              </button>
            )}
          </div>
        )}

      </div>
    </div>
  );
}